import { Download, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist'
import type { DocumentInitParameters } from 'pdfjs-dist/types/src/display/api'
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url'
import { ApiError, apiBlob } from '../api/client'
import type { Report } from '../api/types'
import { Button, LoadingState, Modal } from '../components/ui'
import { PdfReportViewer } from './PdfReportViewer'

type PreviewState = { status: 'loading' } | { status: 'ready'; document: PDFDocumentProxy } | { status: 'error'; message: string }

/** Show the saved version itself: rebuilding HTML would reread every source photograph. */
export function ReportPreviewModal({ report, periodLabel, objectName, downloading, onDownload, onClose }: {
  report: Report
  periodLabel: string
  objectName: string
  downloading: boolean
  onDownload(): void
  onClose(): void
}) {
  const [state, setState] = useState<PreviewState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    let loadingTask: PDFDocumentLoadingTask | null = null

    async function load() {
      try {
        const blob = await apiBlob(`/reports/${encodeURIComponent(report.id)}/download?inline=1`, { timeout: 600_000, signal: controller.signal })
        if (controller.signal.aborted) return
        // Reject a gateway/error document before handing bytes to the PDF parser.
        if (blob.type.split(';')[0].trim().toLowerCase() !== 'application/pdf' || await blob.slice(0, 5).text() !== '%PDF-') {
          throw new Error('Вместо PDF получен другой файл. Повторите загрузку. Если ошибка повторится, сообщите администратору.')
        }
        if (controller.signal.aborted) return
        const [pdfjs, data] = await Promise.all([import('pdfjs-dist'), blob.arrayBuffer()])
        if (controller.signal.aborted) return
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl
        const options: DocumentInitParameters = {
          data: new Uint8Array(data),
          disableFontFace: true,
          useSystemFonts: false,
          useWasm: false,
          enableXfa: false,
        }
        loadingTask = pdfjs.getDocument(options)
        const document = await loadingTask.promise
        if (controller.signal.aborted) return
        setState({ status: 'ready', document })
      } catch (error) {
        if (controller.signal.aborted) return
        const message = error instanceof ApiError
          ? error.message
          : error instanceof Error && error.message.startsWith('Вместо PDF')
            ? error.message
            : navigator.onLine
              ? 'PDF не загрузился. Повторите загрузку или скачайте отчёт.'
              : 'Подключитесь к сети и повторите загрузку отчёта.'
        setState({ status: 'error', message })
      }
    }

    void load()
    return () => {
      controller.abort()
      if (loadingTask) void loadingTask.destroy().catch(() => {})
    }
  }, [report.id, attempt])

  function retry() {
    setState({ status: 'loading' })
    setAttempt((value) => value + 1)
  }

  return <Modal open onClose={onClose} title={`Предпросмотр · версия ${report.version}`} description={`${objectName} · ${periodLabel}`} size="large" className="report-preview-modal">
    <div className="modal__body mg-preview-body">
      {state.status === 'loading' && <LoadingState label="Загружаем PDF для просмотра" />}
      {state.status === 'error' && <div className="error-state" role="alert"><div><strong>Не удалось открыть предпросмотр</strong><p>{state.message}</p></div><Button variant="secondary" onClick={retry}><RefreshCw size={16} />Повторить</Button></div>}
      {state.status === 'ready' && <PdfReportViewer document={state.document} title={`Отчёт ${periodLabel}, версия ${report.version}`} />}
    </div>
    <div className="modal__footer">
      <Button variant="quiet" onClick={onClose}>Закрыть</Button>
      <Button variant="secondary" loading={downloading} onClick={onDownload}><Download size={16} />Скачать PDF</Button>
    </div>
  </Modal>
}
